import { createSlice } from '@reduxjs/toolkit';

import { STORAGE_KEYS } from '@/constants';

import type { CarrierDTO } from '@/service/types/usertypes';
import type {
  AppGuideSlide,
  ConfigurationModel,
  CountryCode,
  AppSettings,
  PlantInfo,
  FunctionCodes,
  MiddlewareFunctionCodes,
} from '@/types';
import type { PayloadAction } from '@reduxjs/toolkit';

// Helper to parse country codes from settings JSON string
function parseCountryCodes(settings: AppSettings): CountryCode[] {
  try {
    const parsed: unknown = JSON.parse(settings.COUNTRY_CODES || '[]');
    return Array.isArray(parsed) ? (parsed as CountryCode[]) : [];
  } catch {
    return [];
  }
}

// Validate that an image URL is safe (reject javascript:, data:, and other dangerous schemes)
function isValidImageUrl(url: string): boolean {
  const lower = url.toLowerCase().trim();
  return (
    !lower.startsWith('javascript:') && !lower.startsWith('data:') && !lower.startsWith('vbscript:')
  );
}

// Helper to parse app guide slides from a JSON string
function parseAppGuideSlidesFromRaw(raw: string | undefined): AppGuideSlide[] {
  try {
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item: unknown): item is AppGuideSlide =>
        typeof item === 'object' &&
        item !== null &&
        'image' in item &&
        typeof (item as Record<string, unknown>)['image'] === 'string' &&
        isValidImageUrl((item as Record<string, unknown>)['image'] as string) &&
        'header' in item &&
        typeof (item as Record<string, unknown>)['header'] === 'string' &&
        'text' in item &&
        typeof (item as Record<string, unknown>)['text'] === 'string'
    );
  } catch {
    return [];
  }
}

// Helper to parse app guide slides from settings JSON string (APP_GUIDE_URL)
function parseAppGuideSlides(settings: AppSettings): AppGuideSlide[] {
  return parseAppGuideSlidesFromRaw(settings.APP_GUIDE_URL);
}

function parseAppGuideSlidesSpanish(settings: AppSettings): AppGuideSlide[] {
  return parseAppGuideSlidesFromRaw(settings.APP_GUIDE_URL_SPANISH);
}

// Helper to parse function codes from settings JSON string
function parseFunctionCodes(settings: AppSettings): FunctionCodes | null {
  try {
    const parsed: unknown = JSON.parse(settings.FUNCTION_CODES || '{}');
    return typeof parsed === 'object' && parsed !== null ? (parsed as FunctionCodes) : null;
  } catch {
    return null;
  }
}

// Helper to parse middleware function codes from settings JSON string
// Matches Xamarin: MiddlewareService uses MIDDLEWARE_FUNCTION_CODES.update_task for all manhattan calls
function parseMiddlewareFunctionCodes(settings: AppSettings): MiddlewareFunctionCodes | null {
  try {
    const parsed: unknown = JSON.parse(settings.MIDDLEWARE_FUNCTION_CODES || '{}');
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as MiddlewareFunctionCodes)
      : null;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────

export type GlobalStatus = 'loading' | 'ready' | 'error' | 'maintenance';

export interface GlobalState {
  status: GlobalStatus;
  config: ConfigurationModel | null;
  countryCodes: CountryCode[];
  settings: AppSettings | null;
  functionCodes: FunctionCodes | null;
  middlewareFunctionCodes: MiddlewareFunctionCodes | null;
  plants: PlantInfo[];
  carriers: CarrierDTO[];
  appGuideSlides: AppGuideSlide[];
  appGuideSlidesSpanish: AppGuideSlide[];
  error: string | null;
}

/**
 * Persist config to localStorage so it survives page refresh.
 */
function persistConfig(config: ConfigurationModel | null): void {
  try {
    if (config !== null) {
      localStorage.setItem(STORAGE_KEYS.globalConfig, JSON.stringify(config));
    } else {
      localStorage.removeItem(STORAGE_KEYS.globalConfig);
    }
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

const VERIFY_DATA_KEY = 'yms_verify_data';

/**
 * Persist verify-otp data (settings with FUNCTION_CODES + plants) to localStorage.
 */
function persistVerifyData(settings: AppSettings | null): void {
  try {
    if (settings !== null) {
      localStorage.setItem(VERIFY_DATA_KEY, JSON.stringify({ settings }));
    } else {
      localStorage.removeItem(VERIFY_DATA_KEY);
    }
    sessionStorage.removeItem(VERIFY_DATA_KEY);
  } catch {
    // Silently fail if storage is unavailable
  }
}

/**
 * Hydrate global state from localStorage synchronously.
 * Plants, settings, and functionCodes are restored from the cached config
 * so PlantSelectionPage renders immediately after page refresh.
 */
function loadInitialState(): GlobalState {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.globalConfig);
    if (raw) {
      const config = JSON.parse(raw) as ConfigurationModel;
      const isMaintenanceMode = config.settings.APP_MAINTENANCE;

      // Verify-OTP settings are the authoritative settings after login — they contain
      // plant-specific values (COMPLETE_TASK_FLOW, MA_ACCESS_REQUEST, etc.) that the
      // bootstrap config may not have. Use them as the primary settings when available.
      let resolvedSettings: AppSettings = config.settings;
      let functionCodes: FunctionCodes | null = null;
      let middlewareFunctionCodes: MiddlewareFunctionCodes | null = null;
      let appGuideSlides: AppGuideSlide[] = [];
      let appGuideSlidesSpanish: AppGuideSlide[] = [];
      const verifyRaw =
        localStorage.getItem(VERIFY_DATA_KEY) ?? sessionStorage.getItem(VERIFY_DATA_KEY);
      if (verifyRaw) {
        const verifyData = JSON.parse(verifyRaw) as { settings: AppSettings; plants: PlantInfo[] };
        resolvedSettings = verifyData.settings;
        functionCodes = parseFunctionCodes(verifyData.settings);
        middlewareFunctionCodes = parseMiddlewareFunctionCodes(verifyData.settings);
        appGuideSlides = parseAppGuideSlides(verifyData.settings);
        appGuideSlidesSpanish = parseAppGuideSlidesSpanish(verifyData.settings);
      }

      return {
        status: isMaintenanceMode ? 'maintenance' : 'ready',
        config,
        countryCodes: parseCountryCodes(config.settings),
        settings: resolvedSettings,
        functionCodes,
        middlewareFunctionCodes,
        carriers: [],
        plants: [],
        appGuideSlides,
        appGuideSlidesSpanish,
        error: isMaintenanceMode ? 'System is under maintenance' : null,
      };
    }
  } catch {
    // Fall through to default
  }

  return {
    status: 'loading',
    config: null,
    countryCodes: [],
    settings: null,
    functionCodes: null,
    middlewareFunctionCodes: null,
    plants: [],
    carriers: [],
    appGuideSlides: [],
    appGuideSlidesSpanish: [],
    error: null,
  };
}

const initialState: GlobalState = loadInitialState();

// ─────────────────────────────────────────────────────────────────
// Slice — client state only, synced from TanStack Query hooks
// ─────────────────────────────────────────────────────────────────

export const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    resetStartup: () => {
      persistConfig(null);
      persistVerifyData(null);
      return {
        status: 'loading' as GlobalStatus,
        config: null,
        countryCodes: [],
        settings: null,
        functionCodes: null,
        middlewareFunctionCodes: null,
        plants: [],
        carriers: [],
        appGuideSlides: [],
        appGuideSlidesSpanish: [],
        error: null,
      };
    },

    // Called by useBootstrapConfigQuery onSuccess to sync server state → Redux
    setBootstrapConfig: (state, action: PayloadAction<ConfigurationModel>) => {
      const config = action.payload;

      // Check for maintenance mode (APP_MAINTENANCE is a string "true" or "false")
      const isMaintenanceMode = config.settings.APP_MAINTENANCE;
      if (isMaintenanceMode) {
        state.status = 'maintenance';
        state.error = 'System is under maintenance';
      } else {
        state.status = 'ready';
      }

      state.config = config;
      state.countryCodes = parseCountryCodes(config.settings);

      // Merge: bootstrap config as base, preserve existing verify-OTP settings as overlay.
      // The verify-OTP response provides plant-specific settings (COMPLETE_TASK_FLOW,
      // MA_ACCESS_REQUEST, etc.) that the generic config endpoint may not include.
      // Without this merge, re-fetching config would wipe out plant-specific settings.
      state.settings = state.settings ? { ...config.settings, ...state.settings } : config.settings;
      persistConfig(config);
    },

    // Called when bootstrap config query fails
    setBootstrapError: (state, action: PayloadAction<string>) => {
      state.status = 'error';
      state.error = action.payload;
    },

    // Called by useCarriersQuery onSuccess to sync server state → Redux
    setCarriers: (state, action: PayloadAction<CarrierDTO[]>) => {
      state.carriers = action.payload;
    },

    setPlants: (state, action: PayloadAction<PlantInfo[]>) => {
      state.plants = action.payload;
    },

    // Called after OTP verification to store function codes and plants
    setVerifyOTPData: (state, action: PayloadAction<{ settings: AppSettings }>) => {
      const { settings } = action.payload;
      state.settings = settings;
      state.functionCodes = parseFunctionCodes(settings);
      state.middlewareFunctionCodes = parseMiddlewareFunctionCodes(settings);
      state.appGuideSlides = parseAppGuideSlides(settings);
      state.appGuideSlidesSpanish = parseAppGuideSlidesSpanish(settings);
      persistVerifyData(settings);
    },
  },
});

export const {
  resetStartup,
  setBootstrapConfig,
  setBootstrapError,
  setCarriers,
  setPlants,
  setVerifyOTPData,
} = globalSlice.actions;
export const selectGlobalStatus = (state: { global: GlobalState }) => state.global.status;
export const selectIsBootstrapComplete = (state: { global: GlobalState }) =>
  state.global.status === 'ready';
export const selectCountryCodes = (state: { global: GlobalState }) => state.global.countryCodes;
export const selectSettings = (state: { global: GlobalState }) => state.global.settings;
export const selectFunctionCodes = (state: { global: GlobalState }) => state.global.functionCodes;
export const selectConfig = (state: { global: GlobalState }) => state.global.config;
export const selectIsMaintenanceMode = (state: { global: GlobalState }) =>
  state.global.status === 'maintenance';
export const selectStartupError = (state: { global: GlobalState }) => state.global.error;
export const selectCarriers = (state: { global: GlobalState }) => state.global.carriers;
export const selectPlants = (state: { global: GlobalState }) => state.global.plants;
export const selectAppGuideSlides = (state: { global: GlobalState }) => state.global.appGuideSlides;
export const selectAppGuideSlidesSpanish = (state: { global: GlobalState }) =>
  state.global.appGuideSlidesSpanish;
export const selectMiddlewareFunctionCodes = (state: { global: GlobalState }) =>
  state.global.middlewareFunctionCodes;
